<script lang="ts">
  import { storyblokEditable } from '@storyblok/svelte';
  import { Grid, GridItem, type GridColumnSpan } from '@athena-lib/Grid';
  import { Carousel, CarouselItem } from '@athena-lib/Carousel';
  import { PageSection } from '@athena-lib/Page';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import type { MaybeResponsive } from '@athena-lib/responsive-utils';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { Button } from '@athena-lib/Button';
  import clsx from 'clsx';
  import type { PageSectionBackground } from '@athena-lib/container-context.svelte';
  import type { GalleryPageSectionBlock } from '@storyblok-types';
  import PageSectionHeading from '../shared/PageSectionHeading.svelte';
  import { isRichTextEmpty } from '../../RichText/types';
  import GalleryMedia from './GalleryMedia.svelte';
  const { blok }: { blok: GalleryPageSectionBlock } = $props();
  const responsiveBackground: MaybeResponsive<PageSectionBackground> = $derived({
    lg: blok.desktopBackgroundColor ?? 'light',
    md: blok.desktopBackgroundColor ?? 'light',
    sm: blok.mobileBackgroundColor ?? 'light',
  });

  const commonTranslations = getCommonTranslations();

  const rows = $derived(parseInt(blok.rows ?? '1'));
  const imagesPerRow = $derived(parseInt(blok.imagesPerRow ?? '1'));
  const imageCount = $derived(rows * imagesPerRow);
  const hasViewAll = $derived(blok.enableViewAll && imageCount < (blok.assets?.length ?? 0));
  let viewAll = $state(false);
  const imageCountToShow = $derived(viewAll ? (blok.assets?.length ?? 0) : imageCount);
</script>

<PageSection
  id={blok.elementId}
  action={storyblokEditable}
  actionArg={blok}
  ySpace={{ top: blok.topPadding ?? 'medium', bottom: blok.bottomPadding ?? 'medium' }}
  backgroundColor={responsiveBackground}
  width={blok.desktopFullWidth ? 'full' : 'large'}
>
  <Stack space="large">
    {#if blok.headingText || !isRichTextEmpty(blok.description)}
      <StackItem>
        <div class="flex justify-center">
          <div class={clsx('w-full max-w-page-max-lg', blok.desktopFullWidth && 'md:px-medium')}>
            <PageSectionHeading
              headingText={blok.headingText}
              headingTag={blok.headingTag}
              headingSize={blok.headingSize}
              description={blok.description}
              mobileAlignment={blok.mobileAlignment}
              desktopAlignment={blok.desktopAlignment}
            />
          </div>
        </div>
      </StackItem>
    {/if}
    <StackItem class="mx-auto w-full max-w-page-max-lg">
      {#if blok.swipeInMobile}
        <div class="md:hidden">
          <Carousel peekNext={true} itemCount={(blok.assets ?? []).length}>
            {#each blok.assets ?? [] as asset}
              <CarouselItem class="aspect-square">
                <GalleryMedia showArtist={blok.showArtist || false} {asset} zoomOnHover={false} />
              </CarouselItem>
            {/each}
          </Carousel>
        </div>
      {/if}
      <div class={blok.swipeInMobile ? 'hidden md:block' : ''}>
        <Grid columns={12} space="gutter">
          {#each Array.from({ length: imageCountToShow }) as _, index}
            <GridItem
              colSpan={{
                sm:
                  blok.twoColumnsInMobile === false || (index === 0 && blok.largeFirstImage)
                    ? 12
                    : 6,
                md: (index === 0 && blok.largeFirstImage
                  ? (12 / imagesPerRow) * 2
                  : 12 / imagesPerRow) as GridColumnSpan,
              }}
              rowSpan={{ sm: 1, md: index === 0 && blok.largeFirstImage ? 2 : 1 }}
              class="aspect-square"
            >
              {#if (blok.assets ?? [])[index]}
                <GalleryMedia
                  showArtist={blok.showArtist || false}
                  asset={blok.assets![index]}
                  zoomOnHover={blok.zoomOnHover ?? false}
                />
              {/if}
            </GridItem>
          {/each}
        </Grid>
      </div>
    </StackItem>
    {#if hasViewAll && !viewAll}
      <StackItem>
        <div class="flex justify-center">
          <Button onClick={() => (viewAll = true)}>{commonTranslations.viewAll}</Button>
        </div>
      </StackItem>
    {/if}
  </Stack>
</PageSection>
